import type { ChangeEvent, FC, FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '../../../api/useTranslation';
import { ChatTabType } from '../../../store/slices/chat/types';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon, Icons } from '../../icons';
import { InputButton } from '../../inputButton/InputButton';
import { InputGroup } from '../../inputGroup/InputGroup';
import { BaseInput } from '../../inputs/baseInput/BaseInput';
import { L12nText } from '../../text';
import { MiniChatInput } from '../miniChatInput/MiniChatInput';

import { SupportingText } from './SupportingText';

import styles from './MessageInput.module.css';

interface MessageInputProps {
  isGameChatBlocked: boolean;
  isGameChatUnavailable: boolean;
  isLiveSupportChatBlocked: boolean;
  onSubmit: (input: string) => void;
  selectedTab: ChatTabType;
  cashierCallback?: () => void;
  characterLimit?: number;
  isMiniChat?: boolean;
}

export const MessageInput: FC<MessageInputProps> = ({
  cashierCallback,
  characterLimit = 200,
  isGameChatBlocked,
  isGameChatUnavailable,
  isLiveSupportChatBlocked,
  isMiniChat,
  onSubmit,
  selectedTab,
}) => {
  const [gameChatInputValue, setGameChatInputValue] = useState(''); // todo: consider moving input value to redux
  const [supportChatInputValue, setSupportChatInputValue] = useState(''); // todo: consider moving input value to redux
  const { translations } = useTranslation();

  const isGameChatTabSelected = selectedTab === ChatTabType.GameChat;
  const isLiveSupportChatTabSelected = selectedTab === ChatTabType.LiveSupportChat;
  const inputValue = isGameChatTabSelected ? gameChatInputValue : supportChatInputValue;
  const setInputValue = isGameChatTabSelected ? setGameChatInputValue : setSupportChatInputValue;
  const isOverLimit = useMemo(() => inputValue.length === characterLimit, [inputValue, characterLimit]);

  const isDisabled =
    ((isGameChatBlocked || isGameChatUnavailable) && (isGameChatTabSelected || isMiniChat)) ||
    (isLiveSupportChatBlocked && isLiveSupportChatTabSelected && !isMiniChat);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.slice(0, characterLimit);
    setInputValue(value);
  };

  const handleSendMessage = useCallback((): void => {
    // Prevent from sending an empty message
    if (inputValue.trim()) {
      onSubmit(inputValue);
      setInputValue('');
    }
  }, [inputValue, onSubmit, setInputValue]);

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    handleSendMessage();
  };

  const getPlaceholder = useCallback((): string => {
    if (isGameChatBlocked && (isGameChatTabSelected || isMiniChat)) {
      return translations['CHAT_IS_BLOCKED'];
    }

    if (isLiveSupportChatBlocked && isLiveSupportChatTabSelected && !isMiniChat) {
      return translations['CHAT_IS_BLOCKED'];
    }

    if (isGameChatUnavailable && (isGameChatTabSelected || isMiniChat)) {
      return translations['CHAT_NOT_AVAILABLE'];
    }

    return translations['TYPE_MESSAGE'];
  }, [
    isGameChatBlocked,
    isGameChatTabSelected,
    isGameChatUnavailable,
    isLiveSupportChatBlocked,
    isLiveSupportChatTabSelected,
    isMiniChat,
    translations,
  ]);

  const shouldShowLowBalanceMessage =
    isGameChatUnavailable && selectedTab !== ChatTabType.LiveSupportChat && !isGameChatBlocked;

  const mods = useModifiers(styles, 'messageInput', {
    miniChat: isMiniChat,
    disabled: isDisabled,
  });

  return (
    <div className={`${styles.messageInput} ${mods}`}>
      <form onSubmit={handleFormSubmit} id="chat-form" autoComplete="off">
        {isMiniChat ? (
          <MiniChatInput
            disabled={isDisabled}
            errorMessage={isOverLimit ? <L12nText tk="CHAT_MAX" /> : null}
            maxLength={characterLimit}
            name="chat-message-input"
            onChange={handleChange}
            placeholder={getPlaceholder()}
            supportingText={shouldShowLowBalanceMessage ? <SupportingText cashierCallback={cashierCallback} /> : null}
            value={inputValue}
          />
        ) : (
          <InputGroup
            input={
              <BaseInput
                disabled={isDisabled}
                errorMessage={isOverLimit ? <L12nText tk="CHAT_MAX" /> : null}
                maxLength={characterLimit}
                name="chat-message-input"
                onChange={handleChange}
                placeholder={getPlaceholder()}
                supportingText={
                  shouldShowLowBalanceMessage ? <SupportingText cashierCallback={cashierCallback} /> : null
                }
                value={inputValue}
              />
            }
          >
            {isDisabled ? null : (
              <InputButton disabled={!inputValue} icon={<Icon icon={Icons.Send} />} onClick={handleSendMessage} />
            )}
          </InputGroup>
        )}
      </form>
    </div>
  );
};
